import React from 'react';
import { motion } from 'framer-motion';

/**
 * Animation de shimmer pour les skeletons
 */
const shimmerAnimation = {
  x: ['-100%', '100%'],
  transition: {
    x: {
      repeat: Infinity,
      repeatType: 'loop',
      duration: 1.5,
      ease: 'easeInOut'
    }
  }
};

/**
 * Skeleton de base avec animation shimmer
 */
export const SkeletonBase = ({ width, height, borderRadius = '8px', className = '', style = {} }) => {
  return (
    <div
      className={`skeleton-base ${className}`}
      style={{
        width: width || '100%',
        height: height || '20px',
        borderRadius,
        backgroundColor: '#e5e7eb',
        position: 'relative',
        overflow: 'hidden',
        ...style
      }}
    >
      <motion.div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
          transform: 'translateX(-100%)'
        }}
        animate={shimmerAnimation}
      />
    </div>
  );
};

/**
 * Skeleton pour une carte d'entreprise
 */
export const BusinessCardSkeleton = () => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        marginBottom: '1rem'
      }}
    >
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
        <SkeletonBase width="120px" height="120px" borderRadius="8px" />
        <div style={{ flex: 1 }}>
          <SkeletonBase width="60%" height="24px" style={{ marginBottom: '0.5rem' }} />
          <SkeletonBase width="40%" height="16px" style={{ marginBottom: '0.5rem' }} />
          <SkeletonBase width="80%" height="16px" />
        </div>
      </div>
      <SkeletonBase width="100%" height="16px" style={{ marginBottom: '0.5rem' }} />
      <SkeletonBase width="70%" height="16px" />
    </div>
  );
};

/**
 * Skeleton pour une liste d'entreprises
 */
export const BusinessListSkeleton = ({ count = 3 }) => {
  return (
    <div>
      {Array.from({ length: count }).map((_, index) => (
        <BusinessCardSkeleton key={index} />
      ))}
    </div>
  );
};

/**
 * Skeleton pour un événement
 */
export const EventCardSkeleton = () => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        marginBottom: '1rem'
      }}
    >
      <SkeletonBase width="100%" height="200px" borderRadius="0" />
      <div style={{ padding: '1rem' }}>
        <SkeletonBase width="40%" height="20px" style={{ marginBottom: '0.5rem' }} />
        <SkeletonBase width="80%" height="24px" style={{ marginBottom: '0.5rem' }} />
        <SkeletonBase width="100%" height="16px" style={{ marginBottom: '0.5rem' }} />
        <SkeletonBase width="90%" height="16px" style={{ marginBottom: '1rem' }} />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <SkeletonBase width="100px" height="32px" borderRadius="16px" />
          <SkeletonBase width="100px" height="32px" borderRadius="16px" />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton pour une liste d'événements
 */
export const EventListSkeleton = ({ count = 3 }) => {
  return (
    <div>
      {Array.from({ length: count }).map((_, index) => (
        <EventCardSkeleton key={index} />
      ))}
    </div>
  );
};

/**
 * Skeleton pour un avatar
 */
export const AvatarSkeleton = ({ size = 40 }) => {
  return <SkeletonBase width={`${size}px`} height={`${size}px`} borderRadius="50%" />;
};

/**
 * Skeleton pour du texte (lignes)
 */
export const TextSkeleton = ({ lines = 3, width = '100%' }) => {
  return (
    <div>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonBase
          key={index}
          width={index === lines - 1 ? '60%' : width}
          height="16px"
          style={{ marginBottom: '0.5rem' }}
        />
      ))}
    </div>
  );
};

/**
 * Skeleton pour un bouton
 */
export const ButtonSkeleton = ({ width = '120px', height = '40px' }) => {
  return <SkeletonBase width={width} height={height} borderRadius="8px" />;
};

export default SkeletonBase;

